import React from "react";
import { FunnelStep, PathKey } from "./types";
import { fmtN } from "./format";

// Spreadsheet chrome for the Growth Funnel tab: an Excel-looking table
// (column letters, row numbers, one green header row) and the "fx" line above
// it that says the sheet's one finding in plain words. Founder pick 9/21
// ("make it a simple Excel-looking spreadsheet"): no bars, no tiles, the
// yellow cell marks where the most PEOPLE were lost, never a percent, and
// never red — losing people at a step is normal.

export type Cell = { v: React.ReactNode; cls?: string; span?: number; title?: string };
export type Row = { cells: Cell[]; cls?: string };

const letters = (n: number) => Array.from({ length: n }, (_, i) => String.fromCharCode(65 + i));

export function Fx({ children }: { children: React.ReactNode }) {
  return (
    <div className="gr-fx">
      <i>fx</i>
      <span>{children}</span>
    </div>
  );
}

// `head` rows come first (numbered from 1), then `rows`; `cols` is the count
// of real columns (the row-number gutter is added here).
export function Sheet({ cols, head, rows, label }: { cols: number; head: Row[]; rows: Row[]; label: string }) {
  let n = 0;
  const tr = (r: Row, isHead: boolean) => (
    <tr className={r.cls} key={n}>
      <td className="gr-sh-rn">{++n}</td>
      {r.cells.map((c, i) =>
        isHead ? (
          <th key={i} colSpan={c.span} className={c.cls}>
            {c.v}
          </th>
        ) : (
          <td key={i} colSpan={c.span} className={c.cls} title={c.title}>
            {c.v}
          </td>
        )
      )}
    </tr>
  );
  return (
    <div className="gr-scroll-x">
      <table className="gr-sheet" aria-label={label}>
        <thead>
          <tr className="gr-sh-letters">
            <th className="gr-sh-rn" />
            {letters(cols).map((l) => (
              <th key={l}>{l}</th>
            ))}
          </tr>
          {head.map((r) => tr(r, true))}
        </thead>
        <tbody>{rows.map((r) => tr(r, false))}</tbody>
      </table>
    </div>
  );
}

// ---- funnel math ------------------------------------------------------------
//
// A funnel is [visitors, chose path, …, activated]. Row 0 is context (most
// visitors are fans, not talent who gave up) and row 1 is where the path
// starts, so "lost" exists from row 2 on. A join is not comparable when either
// side is a web+app combined count in a platform column, or when the later
// step has MORE people (backfilled history has no visit data).

export function lostAt(steps: FunnelStep[], i: number): number | null {
  if (i < 2) return null;
  const a = steps[i - 1];
  const b = steps[i];
  if (!a || !b || a.combined || b.combined) return null;
  const lost = a.n - b.n;
  return lost < 0 ? null : lost;
}

export function keptAt(steps: FunnelStep[], i: number): number | null {
  if (lostAt(steps, i) == null) return null;
  const prev = steps[i - 1].n;
  return prev > 0 ? Math.round((steps[i].n / prev) * 100) : null;
}

// Index of the row that lost the most people, or -1 when nothing was lost.
export function leakAt(steps: FunnelStep[]): number {
  let best = 0;
  let at = -1;
  for (let i = 2; i < steps.length; i++) {
    const l = lostAt(steps, i);
    if (l != null && l > best) {
      best = l;
      at = i;
    }
  }
  return at;
}

// Chose path → last step, as a whole percent (null when nobody chose).
export function endToEnd(steps: FunnelStep[], field: "n" | "prev" = "n"): number | null {
  const chose = steps[1]?.[field] || 0;
  const last = steps[steps.length - 1]?.[field] || 0;
  return chose > 0 ? Math.round((last / chose) * 1000) / 10 : null;
}

// Talent wording in copy ("Talent", never "DJs"); the server's step keys are
// the contract, its labels are only a fallback.
export function stepLabel(s: FunnelStep, path: PathKey): string {
  if (s.key === "landed") return "Visitors";
  if (s.key === "path_selected") return path === "dj" ? "Chose talent" : "Chose fan";
  return s.label;
}

export function leakSentence(steps: FunnelStep[], path: PathKey): string | null {
  const k = leakAt(steps);
  if (k < 0) return null;
  const lost = lostAt(steps, k) as number;
  return `${fmtN(lost)} of ${fmtN(steps[k - 1].n)} who reached "${stepLabel(steps[k - 1], path)}" did not reach "${stepLabel(steps[k], path)}"`;
}
